import {
  Building2,
  CheckCircle2,
  ChevronRight,
  Clock,
  Gavel,
  RefreshCw,
  User,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type Task = {
  id: string;
  module: string;
  entityType: string;
  entityId: string;
  taskType: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  orgId: string | null;
  orgName: string | null;
  orgSlug: string | null;
};

type Tab = "open" | "closed" | "all";

export function OppgaverPage() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>("open");
  const [acting, setActing] = useState<string | null>(null);

  function load() {
    setLoading(true);
    fetch(`${API}/api/admin/tasks`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setTasks(Array.isArray(d) ? d : []))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  const filtered = tasks.filter((t) => {
    if (tab === "open") return t.status === "open";
    if (tab === "closed") return t.status === "closed";
    return true;
  });

  const openTasks = tasks.filter((t) => t.status === "open");
  const closedCount = tasks.filter((t) => t.status === "closed").length;

  async function updateStatus(taskId: string, status: string) {
    setActing(taskId);
    try {
      const r = await fetch(`${API}/api/admin/tasks/${taskId}/status`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (r.ok) {
        const updated = await r.json();
        setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status: updated.status } : t)));
      }
    } finally {
      setActing(null);
    }
  }

  function entityLink(task: Task) {
    if (task.entityType === "auction") return `/auksjoner/${task.entityId}`;
    if (task.entityType === "organization") return `/organisasjoner/${task.entityId}`;
    if (task.entityType === "bank_account_request") return task.orgId ? `/organisasjoner/${task.orgId}` : null;
    if (task.entityType === "user") return `/brukere/${task.entityId}`;
    return null;
  }

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-foreground" style={{ fontFamily: "var(--font-display)" }}>
            Oppgaver
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Administrer og behandle innkommende oppgaver
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Oppdater
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList>
          <TabsTrigger value="open">Åpne ({openTasks.length})</TabsTrigger>
          <TabsTrigger value="closed">Lukkede ({closedCount})</TabsTrigger>
          <TabsTrigger value="all">Alle ({tasks.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Task list */}
      {loading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-16 rounded-xl bg-muted/40 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <CheckCircle2 className="h-12 w-12 text-muted-foreground/20 mb-4" />
          <p className="text-sm font-medium text-muted-foreground">Ingen oppgaver her</p>
          {tab === "open" && (
            <p className="text-xs text-muted-foreground/60 mt-1">Alt er i orden!</p>
          )}
        </div>
      ) : (
        <Card className="overflow-hidden">
          <div className="divide-y divide-border">
            {filtered.map((task) => {
              const link = entityLink(task);
              const isAuctionReview = task.taskType === "approval_review" && task.module === "auctions";
              const isActing = acting === task.id;

              return (
                <div
                  key={task.id}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-muted/20 transition-colors cursor-pointer"
                  onClick={() => isAuctionReview ? navigate(`/auksjoner/${task.entityId}`) : navigate(`/oppgaver/${task.id}`)}
                >
                  <TaskIcon taskType={task.taskType} module={task.module} status={task.status} />

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-foreground text-sm">{task.title}</p>
                      <TypeBadge taskType={task.taskType} module={task.module} />
                      <PriorityBadge priority={task.priority} />
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {task.orgId ? (
                        <button
                          className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                          onClick={(e) => { e.stopPropagation(); navigate(`/organisasjoner/${task.orgId}`); }}
                        >
                          <Building2 className="h-3 w-3" />
                          {task.orgName}
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          Ikke tilknyttet org
                        </span>
                      )}
                      <span className="text-muted-foreground/40 text-xs">·</span>
                      <span className="text-xs text-muted-foreground">{formatDate(task.updatedAt)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    {task.status === "closed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 rounded-lg text-xs gap-1.5 text-muted-foreground"
                        disabled={isActing}
                        onClick={() => updateStatus(task.id, "open")}
                      >
                        <Clock className="h-3.5 w-3.5" />
                        Gjenåpne
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 rounded-lg text-xs gap-1 text-muted-foreground px-2"
                      disabled={!link}
                      onClick={() => { if (link) navigate(link); }}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}



/* ─── Helpers ─── */
function TaskIcon({ taskType, module: mod, status }: { taskType: string; module: string; status: string }) {
  if (status === "closed") {
    return (
      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 flex-shrink-0">
        <CheckCircle2 className="h-4.5 w-4.5 text-emerald-600" />
      </div>
    );
  }
  if (mod === "auctions" || taskType === "auction_review" || taskType === "approval_review") {
    return <IconBox className="bg-violet-50"><Gavel className="h-4 w-4 text-violet-600" /></IconBox>;
  }
  if (taskType === "org_registration" || taskType === "org_registration_duplicate" || taskType === "org_approval" || mod === "organizations" || mod === "registrations") {
    return <IconBox className="bg-blue-50"><Building2 className="h-4 w-4 text-blue-600" /></IconBox>;
  }
  if (taskType === "user_review" || mod === "users") {
    return <IconBox className="bg-amber-50"><User className="h-4 w-4 text-amber-600" /></IconBox>;
  }
  return <IconBox className="bg-muted"><Clock className="h-4 w-4 text-muted-foreground" /></IconBox>;
}

function IconBox({ children, className }: { children: React.ReactNode; className: string }) {
  return <div className={`flex h-9 w-9 items-center justify-center rounded-xl flex-shrink-0 ${className}`}>{children}</div>;
}

function PriorityBadge({ priority }: { priority: string }) {
  const map: Record<string, { label: string; variant: any }> = {
    high: { label: "Høy", variant: "destructive" },
    medium: { label: "Medium", variant: "warning" },
    normal: { label: "Normal", variant: "muted" },
    low: { label: "Lav", variant: "muted" },
  };
  const { label, variant } = map[priority] ?? { label: priority, variant: "muted" };
  return <Badge variant={variant}>{label}</Badge>;
}

function TypeBadge({ taskType, module: mod }: { taskType: string; module: string }) {
  if (taskType === "approval_review" && mod === "auctions") return <Badge variant="secondary">Auksjonsgodkjenning</Badge>;
  if (taskType === "org_registration") return <Badge variant="warning">Ny registrering</Badge>;
  if (taskType === "org_registration_duplicate") return <Badge variant="secondary">Allerede kunde</Badge>;
  if (taskType === "bank_account_approval") return <Badge variant="secondary">Kontonummer</Badge>;
  if (taskType === "org_approval") return <Badge variant="secondary">Org-godkjenning</Badge>;
  if (taskType === "user_review") return <Badge variant="secondary">Brukergjennomgang</Badge>;
  return <Badge variant="secondary">{taskType}</Badge>;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("nb-NO", { day: "numeric", month: "short", year: "numeric" });
}
